import React, { useState, useEffect } from 'react';
import { 
  generateProRataPurchaseReport, 
  generateProRataPurchaseSummaryReport, 
  generateProRataFullReport, 
  generateProRataCustomerBills,
  getDairyInfo
} from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileAlt, 
  faFileInvoice, 
  faFileContract, 
  faFilePdf, 
  faCalendarAlt, 
  faTimes, 
  faSpinner 
} from '@fortawesome/free-solid-svg-icons';
import './ProRataReportGenerator.css';

const ProRataReportGenerator = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dairyName, setDairyName] = useState('');

  // Report types
  const reportTypes = [
    { id: 'full', name: 'Full Report', icon: faFileAlt, description: 'Comprehensive report with purchase, summary and bills' },
    { id: 'purchase', name: 'Purchase Report', icon: faFileInvoice, description: 'Daily purchase totals for pro-rata collections' },
    { id: 'summary', name: 'Summary Report', icon: faFileContract, description: 'Customer-wise purchase summaries' },
    { id: 'bills', name: 'Milk Bills', icon: faFilePdf, description: 'Milk bills for all customers' }
  ];

  // Fetch dairy info when component mounts
  useEffect(() => {
    const fetchDairyInfo = async () => {
      try {
        const dairyInfo = await getDairyInfo();
        if (dairyInfo && dairyInfo.dairy_name) {
          // Convert to lowercase and replace spaces with underscores for filename
          setDairyName(dairyInfo.dairy_name.toLowerCase().replace(/\s+/g, '_'));
        }
      } catch (error) {
        console.error('Error fetching dairy info:', error);
        setDairyName('dairy');
      }
    };
    
    fetchDairyInfo();
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
    setSelectedReport(null);
    setShowDateFilter(false);
    setError(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setShowDateFilter(false);
    setSelectedReport(null);
    setFromDate('');
    setToDate('');
    setError(null);
  };

  const handleSelectReport = (report) => {
    setSelectedReport(report);
    setShowDateFilter(true);
  };

  const backToReportSelection = () => {
    setSelectedReport(null);
    setShowDateFilter(false);
    setFromDate('');
    setToDate('');
    setError(null);
  };

  const formatDateForAPI = (dateString) => {
    // Convert YYYY-MM-DD to DD-MM-YYYY for API
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const formatDateForFilename = (dateString) => {
    // Convert YYYY-MM-DD to MonthName-Day format (e.g., April30)
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthName = monthNames[date.getMonth()];
    const day = date.getDate();
    
    return `${monthName}${day}`;
  };

  const getReportTypeString = (reportId) => {
    switch (reportId) {
      case 'full':
        return 'full_report';
      case 'purchase':
        return 'purchase_report';
      case 'summary':
        return 'summary_report';
      case 'bills':
        return 'milk_bills';
      default:
        return reportId;
    }
  };

  const downloadReport = async () => {
    if (!fromDate || !toDate) {
      setError('Please select both start and end dates');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Format dates for API (DD-MM-YYYY)
      const formattedFromDateForAPI = formatDateForAPI(fromDate);
      const formattedToDateForAPI = formatDateForAPI(toDate);
      
      let response;
      
      switch (selectedReport.id) {
        case 'purchase':
          response = await generateProRataPurchaseReport(formattedFromDateForAPI, formattedToDateForAPI);
          break;
        case 'summary':
          response = await generateProRataPurchaseSummaryReport(formattedFromDateForAPI, formattedToDateForAPI);
          break;
        case 'full':
          response = await generateProRataFullReport(formattedFromDateForAPI, formattedToDateForAPI);
          break;
        case 'bills':
          response = await generateProRataCustomerBills(formattedFromDateForAPI, formattedToDateForAPI);
          break;
        default:
          throw new Error('Invalid report type selected');
      }
      
      // Create a blob from the response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.href = url;
      
      // Format dates for filename (MonthName-Day)
      const formattedFromDateForFilename = formatDateForFilename(fromDate);
      const formattedToDateForFilename = formatDateForFilename(toDate);
      const year = new Date(toDate).getFullYear();
      const reportType = getReportTypeString(selectedReport.id);
      
      // Set the download file name with format: dairy_name-report_type-FromDate-ToDate-Year
      const fileName = `${dairyName}-${reportType}-${formattedFromDateForFilename}-${formattedToDateForFilename}-${year}.pdf`;
      link.setAttribute('download', fileName);
      
      // Append the link to the document body
      document.body.appendChild(link);
      
      // Click the link to start the download
      link.click();
      
      // Clean up: remove the link element and revoke the blob URL
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Close the modal after successful download
      closeModal();
    } catch (err) {
      let errorMessage = 'Failed to generate report';
      
      if (err.error) {
        errorMessage = err.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pro-rata-report-generator">
      <button className="report-button" onClick={openModal}>
        <FontAwesomeIcon icon={faFileAlt} />
        Generate Pro-Rata Report
      </button>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="report-modal-content">
            <div className="report-modal-header">
              <h2>
                {showDateFilter 
                  ? `Generate ${selectedReport.name}`
                  : 'Select Report Type'}
              </h2>
              <button className="modal-close-button" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {showDateFilter ? (
              <div className="report-date-filter-container">
                <div className="back-button-container">
                  <button className="back-to-reports-button" onClick={backToReportSelection}>
                    Back to Report Selection
                  </button>
                </div>

                <div className="report-info">
                  <FontAwesomeIcon icon={selectedReport.icon} className="report-icon" />
                  <div className="report-info-text">
                    <h3>{selectedReport.name}</h3>
                    <p>{selectedReport.description}</p>
                  </div>
                </div>

                <div className="date-inputs-container">
                  <div className="date-input-group">
                    <label>From Date</label>
                    <div className="date-input-wrapper" onClick={() => document.getElementById('from-date-input').showPicker()}>
                      <FontAwesomeIcon icon={faCalendarAlt} className="date-icon" />
                      <input 
                        id="from-date-input"
                        type="date" 
                        className="date-input" 
                        value={fromDate} 
                        onChange={(e) => setFromDate(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="date-input-group">
                    <label>To Date</label>
                    <div className="date-input-wrapper" onClick={() => document.getElementById('to-date-input').showPicker()}>
                      <FontAwesomeIcon icon={faCalendarAlt} className="date-icon" />
                      <input 
                        id="to-date-input"
                        type="date" 
                        className="date-input" 
                        value={toDate} 
                        onChange={(e) => setToDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {error && <div className="report-error-message">{error}</div>}

                <div className="report-actions">
                  <button 
                    className="secondary-button" 
                    onClick={backToReportSelection}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    className="primary-button" 
                    onClick={downloadReport}
                    disabled={loading || !fromDate || !toDate}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                        Generating...
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="report-selection-container">
                <div className="report-types-grid">
                  {reportTypes.map(report => (
                    <div 
                      key={report.id} 
                      className="report-type-card"
                      onClick={() => handleSelectReport(report)}
                    >
                      <FontAwesomeIcon icon={report.icon} className="report-icon" />
                      <h3>{report.name}</h3>
                      <p>{report.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProRataReportGenerator; 